'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Variant = 'mouse-test' | 'double-click' | 'scroll';

const BUTTONS = ['Left', 'Middle', 'Right', 'Back', 'Forward'];
// Two presses of the same button closer than this are almost never deliberate: a worn
// switch "chattering" registers one press as two.
const CHATTER_MS = 80;

type Click = { button: number; gap: number | null; at: number };

export default function BrowserMouseTest({
  config,
}: {
  config?: { toolSlug?: string; variant?: string; heading?: string };
}) {
  const variant = (config?.variant ?? 'mouse-test') as Variant;
  const [counts, setCounts] = useState([0, 0, 0, 0, 0]);
  const [down, setDown] = useState<boolean[]>([false, false, false, false, false]);
  const [clicks, setClicks] = useState<Click[]>([]);
  const [chatter, setChatter] = useState([0, 0, 0, 0, 0]);
  const [dblcl, setDblcl] = useState(0);
  const [wheel, setWheel] = useState({ up: 0, down: 0, left: 0, right: 0, reversals: 0, mode: '' });
  const [hz, setHz] = useState<number | null>(null);
  const [peakHz, setPeakHz] = useState(0);
  const last = useRef<number[]>([0, 0, 0, 0, 0]);
  const lastDir = useRef(0);
  const lastWheelAt = useRef(0);
  const moves = useRef<number[]>([]);
  const area = useRef<HTMLDivElement>(null);
  const reported = useRef(false);
  const touch = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? 'mouse-test', '').catch(() => undefined);
  };

  useEffect(() => {
    const el = area.current;
    if (!el) return;
    // Wheel must be non-passive to stop the page scrolling while testing.
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      touch();
      const now = performance.now();
      const dir = Math.sign(e.deltaY);
      setWheel((w) => {
        const next = { ...w, mode: ['pixels', 'lines', 'pages'][e.deltaMode] ?? '' };
        if (e.deltaY < 0) next.up += 1;
        if (e.deltaY > 0) next.down += 1;
        if (e.deltaX < 0) next.left += 1;
        if (e.deltaX > 0) next.right += 1;
        // A single opposite tick inside a quick scroll in one direction is the classic
        // symptom of a failing wheel encoder.
        if (dir && lastDir.current && dir !== lastDir.current && now - lastWheelAt.current < 120)
          next.reversals += 1;
        return next;
      });
      if (dir) lastDir.current = dir;
      lastWheelAt.current = now;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      const now = performance.now();
      moves.current = moves.current.filter((t) => now - t < 1000);
      const n = moves.current.length;
      setHz(n > 20 ? n : null);
      if (n > 20) setPeakHz((p) => Math.max(p, n));
    }, 250);
    return () => window.clearInterval(id);
  }, []);

  // mousedown, not pointerdown: pointer events fire only for the first button of a chord,
  // so a second button pressed while the first is held would be missed.
  const onDown = (e: React.MouseEvent) => {
    e.preventDefault();
    touch();
    const b = e.button;
    if (b < 0 || b > 4) return;
    const now = performance.now();
    const gap = last.current[b] ? now - last.current[b] : null;
    last.current[b] = now;
    setCounts((c) => c.map((v, i) => (i === b ? v + 1 : v)));
    setDown((d) => d.map((v, i) => (i === b ? true : v)));
    if (gap !== null && gap < CHATTER_MS) setChatter((c) => c.map((v, i) => (i === b ? v + 1 : v)));
    setClicks((list) => [{ button: b, gap, at: now }, ...list].slice(0, 30));
  };
  const onUp = (e: React.MouseEvent) => {
    const b = e.button;
    setDown((d) => d.map((v, i) => (i === b ? false : v)));
  };
  const onMove = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    // Coalesced events are the real hardware reports merged into one frame.
    const native = e.nativeEvent as PointerEvent & { getCoalescedEvents?: () => PointerEvent[] };
    const n = native.getCoalescedEvents?.().length || 1;
    const now = performance.now();
    for (let i = 0; i < n; i += 1) moves.current.push(now);
  };

  const reset = () => {
    setCounts([0, 0, 0, 0, 0]);
    setChatter([0, 0, 0, 0, 0]);
    setClicks([]);
    setDblcl(0);
    setWheel({ up: 0, down: 0, left: 0, right: 0, reversals: 0, mode: '' });
    setPeakHz(0);
    last.current = [0, 0, 0, 0, 0];
  };

  const pad = (
    <div
      aria-label="Mouse test area: click, scroll and move here"
      onAuxClick={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
      onDoubleClick={() => setDblcl((d) => d + 1)}
      onMouseDown={onDown}
      onMouseUp={onUp}
      onPointerMove={onMove}
      ref={area}
      role="application"
      style={{
        border: '2px dashed currentColor',
        borderRadius: 12,
        minHeight: 220,
        display: 'grid',
        placeItems: 'center',
        userSelect: 'none',
        cursor: 'crosshair',
        padding: '1rem',
        touchAction: 'none',
      }}
    >
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        {BUTTONS.map((name, i) => (
          <div
            key={name}
            style={{
              minWidth: '4.6rem',
              padding: '0.6rem 0.4rem',
              border: '1px solid currentColor',
              borderRadius: 8,
              textAlign: 'center',
              background: down[i]
                ? 'var(--accent, #2563eb)'
                : counts[i]
                  ? 'rgba(22,163,74,0.18)'
                  : 'transparent',
              color: down[i] ? '#fff' : 'inherit',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <div style={{ fontWeight: 700 }}>{name}</div>
            <div>{counts[i]}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const wheelBox = (
    <div className="tool-html-meta-grid">
      {[
        ['Scroll up', String(wheel.up)],
        ['Scroll down', String(wheel.down)],
        ['Tilt left / right', `${wheel.left} / ${wheel.right}`],
        ['Wrong-direction ticks', String(wheel.reversals)],
        ['Wheel reports in', wheel.mode || '—'],
      ].map(([k, v]) => (
        <div className="tool-live-field" key={k}>
          <span>{k}</span>
          <input readOnly type="text" value={v} />
        </div>
      ))}
    </div>
  );

  const chatterTotal = chatter.reduce((a, b) => a + b, 0);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Mouse</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Mouse test'}</h2>
        <p className="tool-live-subtitle">
          {variant === 'scroll'
            ? 'Scroll the wheel inside the box, slowly in one direction, then fast.'
            : variant === 'double-click'
              ? 'Single-click the button you want to test, at a normal pace, inside the box.'
              : 'Click every button, scroll and move the mouse inside the box.'}
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          {pad}
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={reset} type="button">
              Reset
            </button>
          </div>
        </section>
        <section className="tool-live-preview">
          {variant === 'scroll' ? (
            <>
              {wheelBox}
              <p className="tool-live-panel-note">
                {wheel.reversals
                  ? `${wheel.reversals} tick${wheel.reversals === 1 ? '' : 's'} went the wrong way during a scroll. A few can come from your finger; repeated jumps while scrolling steadily point to a dirty or worn wheel encoder.`
                  : 'Every tick so far went the way you scrolled. A healthy wheel never jumps backwards.'}
              </p>
            </>
          ) : variant === 'double-click' ? (
            <>
              <div className="tool-html-meta-grid">
                {[
                  ['Clicks', String(counts[0] + counts[1] + counts[2])],
                  ['Unintended double clicks', String(chatterTotal)],
                  ['Browser double-click events', String(dblcl)],
                ].map(([k, v]) => (
                  <div className="tool-live-field" key={k}>
                    <span>{k}</span>
                    <input readOnly type="text" value={v} />
                  </div>
                ))}
              </div>
              <p className="tool-live-panel-note">
                {chatterTotal
                  ? `Found ${chatterTotal} press${chatterTotal === 1 ? '' : 'es'} arriving under ${CHATTER_MS} ms after the previous one. Nobody clicks that fast on purpose: the switch is chattering, the usual cause of a mouse that double-clicks by itself.`
                  : `No presses under ${CHATTER_MS} ms apart. Keep clicking normally for a minute; a failing switch fails intermittently.`}
              </p>
              {clicks.length ? (
                <p className="tool-live-panel-note" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  Latest gaps:{' '}
                  {clicks
                    .filter((c) => c.gap !== null)
                    .slice(0, 12)
                    .map(
                      (c) =>
                        `${BUTTONS[c.button]} ${Math.round(c.gap ?? 0)} ms${(c.gap ?? 999) < CHATTER_MS ? ' ⚠' : ''}`,
                    )
                    .join(' · ')}
                </p>
              ) : null}
            </>
          ) : (
            <>
              <div className="tool-html-meta-grid">
                {[
                  ['Polling rate now', hz ? `${hz} Hz` : 'Move the mouse quickly'],
                  ['Highest polling rate seen', peakHz ? `${peakHz} Hz` : '—'],
                  ['Unintended double clicks', String(chatterTotal)],
                  ['Scroll up / down', `${wheel.up} / ${wheel.down}`],
                ].map(([k, v]) => (
                  <div className="tool-live-field" key={k}>
                    <span>{k}</span>
                    <input readOnly type="text" value={v} />
                  </div>
                ))}
              </div>
              <p className="tool-live-panel-note">
                Buttons that light up work. Back and Forward only register on mice that have side
                buttons. Polling rate is estimated from the movement reports the browser receives;
                browsers can cap it, so treat numbers above 1,000 Hz as a lower bound.
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
